import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { switchToTeam } from '../../lib/team-utils';
import {
  Clock, Calendar, Target,
  DollarSign, Shield, Eye, Crown, Copy, UserMinus, ChevronDown,
  Users, Building2, Zap, MessageSquare, 
  Plus, Check, X, Edit2
} from 'lucide-react';
import {
  useStore, type TeamRole,
} from '../../store/useStore';
import { JoinTeamModal } from '../JoinTeamModal';
import { CreateTeamModal } from '../CreateTeamModal';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import {
  ResponsiveContainer,
  Tooltip,
  PieChart, Pie, Cell,
} from 'recharts';
import { toast } from 'react-hot-toast';
import { formatCurrency, formatCompactNumber } from '../../lib/format';

const ROLE_ICONS: Record<TeamRole, React.ElementType> = { admin: Crown, member: Shield, viewer: Eye };

interface UserTeam {
  teamId: string;
  teamName: string;
  role: string;
  isCurrent: boolean;
}

export function TeamOverviewTab() {
  const {
    team, leads, teamConfig, milestones, currentUser,
    addTeamMember, removeTeamMember,
    updateMilestone, addMilestone, deleteMilestone, canAddMember
  } = useStore();

  const navigate = useNavigate();
  const [showInvite, setShowInvite] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTeamSwitcher, setShowTeamSwitcher] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any | null>(null);
  const [userTeams, setUserTeams] = useState<UserTeam[]>([]);
 
  const [newMilestone, setNewMilestone] = useState<{
    label: string;
    target: number;
    type: 'leads' | 'deals' | 'revenue' | 'other';
    current: number;
    startDate?: string;
    targetDate?: string;
  }>({
    label: '',
    target: 0,
    type: 'leads',
    current: 0,
    startDate: new Date().toISOString().split('T')[0],
    targetDate: ''
  });

  const [justCompleted, setJustCompleted] = useState<Record<string, boolean>>({});

  // Detect newly completed milestones to trigger the pulse animation
  useEffect(() => {
    milestones.forEach(m => {
      const progress = Math.min(100, Math.round((m.current / m.target) * 100));
      if (progress >= 100 && !justCompleted[m.id]) {
        setJustCompleted(prev => ({ ...prev, [m.id]: true }));
        // Turn off the animation state after 2 seconds
        setTimeout(() => {
          setJustCompleted(prev => ({ ...prev, [m.id]: false }));
        }, 2000);
      }
    });
  }, [milestones]);

  const [newMember, setNewMember] = useState({
    name: '', role: '', email: '', phone: '',
    teamRole: 'member' as TeamRole,
  });

  const formatTarget = (val: number, type: string) => {
    if (type === 'revenue') return formatCurrency(val);
    return formatCompactNumber(val);
  };


  // Helper to get deal value (Consistent with Dashboard)
  const getDealValue = (l: any) => {
    const offer = Number(l.offerAmount);
    const est = Number(l.estimatedValue);
    if (!isNaN(offer) && offer > 0) return offer;
    if (!isNaN(est) && est > 0) return est;
    return 0;
  };

  const contributionData = team.map(m => {
    const memberDeals = leads.filter(l => 
      (l.assignedTo === m.id || l.assignedTo === m.name) && 
      l.status === 'closed-won'
    );
    const memberRev = memberDeals.reduce((sum, l) => sum + getDealValue(l), 0);
    return { name: m.name, id: m.id, value: memberRev || 0.01, rawRev: memberRev };
  }).sort((a, b) => b.value - a.value);

  const COLORS = ['var(--t-primary)', 'var(--t-accent)', 'var(--t-info)', 'var(--t-warning)', 'var(--t-error)'];

  const handleUpdateMilestone = (milestone: any) => {
    setEditingMilestone({
      ...milestone,
      startDate: milestone.startDate ? new Date(milestone.startDate).toISOString().split('T')[0] : '',
      targetDate: milestone.targetDate ? new Date(milestone.targetDate).toISOString().split('T')[0] : ''
    });
  };

  const handleSaveEditMilestone = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMilestone) return;

    updateMilestone(editingMilestone.id, {
      label: editingMilestone.label,
      target: editingMilestone.target,
      type: editingMilestone.type,
      current: editingMilestone.current,
      startDate: editingMilestone.startDate ? new Date(editingMilestone.startDate).toISOString() : undefined,
      targetDate: editingMilestone.targetDate ? new Date(editingMilestone.targetDate).toISOString() : undefined
    });

    setEditingMilestone(null);
    toast.success('Milestone updated');
  };

  // 0. Page Scroll Position (Fix for "Team Dashboard opens to bottom")
  useEffect(() => {
    const handleScroll = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      const main = document.querySelector('main');
      if (main) main.scrollTo({ top: 0, behavior: 'instant' });
    };
    handleScroll();
    // Re-fire after short delays to ensure content is rendered
    const timer1 = setTimeout(handleScroll, 100);
    const timer2 = setTimeout(handleScroll, 500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Real-time countdown refresh
  const [nowTime, setNowTime] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowTime(Date.now()), 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // 1. Performance Milestones: Real-time sync with leads data
  useEffect(() => {
    if (!milestones.length) return;

    milestones.forEach(m => {
      // Filter leads by startDate if specified
      // Helper to determine if lead was won in milestone period
      const wasWonInMilestone = (l: any, start: Date) => {
        if (l.status !== 'closed-won') return false;
        const winEntry = l.statusHistory?.find((h: any) => h.toStatus === 'closed-won');
        const winDate = winEntry ? new Date(winEntry.timestamp) : new Date(l.updatedAt);
        return winDate >= start;
      };

      const mStart = m.startDate ? new Date(m.startDate) : new Date(0);
      let calculatedVal = m.current;

      if (m.type === 'leads') {
        calculatedVal = leads.filter(l => new Date(l.createdAt) >= mStart).length;
      } else if (m.type === 'deals') {
        // Active deals = negotiating, under contract, or won in period
        calculatedVal = leads.filter(l => {
          if (wasWonInMilestone(l, mStart)) return true;
          return ['contract-in', 'under-contract', 'negotiating'].includes(l.status);
        }).length;
      } else if (m.type === 'revenue') {
        calculatedVal = leads.filter(l => wasWonInMilestone(l, mStart)).reduce((sum, l) => sum + getDealValue(l), 0);
      }

      if (calculatedVal !== m.current) {
        // Only update if it's a real data sync milestone
        if (['leads', 'deals', 'revenue'].includes(m.type)) {
           updateMilestone(m.id, { current: calculatedVal });
        }
      }
    });
  }, [leads, milestones.length, updateMilestone]); // Sync on any leads change or milestone addition

  const getCountdown = (targetDate?: string) => {
    if (!targetDate) return null;
    const target = new Date(targetDate).getTime();
    const diff = target - nowTime;
    if (diff <= 0) return 'Expired';

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30.44);
    const years = Math.floor(days / 365.25);

    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months % 12 > 0) parts.push(`${months % 12}m`);
    if (weeks % 4 > 0) parts.push(`${weeks % 4}w`);
    if (days % 7 > 0) parts.push(`${days % 7}d`);
    if (hours % 24 > 0) parts.push(`${hours % 24}h`);
    if (minutes % 60 > 0) parts.push(`${minutes % 60}m`);

    return parts.slice(0, 3).join(' '); // Show top 3 most relevant units
  };
 
  const handleAddMilestone = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMilestone.label || !newMilestone.target) {
      toast.error('Label and Target are required');
      return;
    }
    addMilestone({
      ...newMilestone,
      startDate: newMilestone.startDate ? new Date(newMilestone.startDate).toISOString() : new Date().toISOString(),
      targetDate: newMilestone.targetDate || undefined
    });
    setNewMilestone({ label: '', target: 0, type: 'leads', current: 0, startDate: new Date().toISOString().split('T')[0], targetDate: '' });
    setShowAddMilestone(false);
    toast.success('Strategic goal added');
  };
 
  const handleDeleteMilestone = (id: string) => {
    if (window.confirm('Decommission this objective?')) {
      deleteMilestone(id);
      toast.success('Strategic goal removed');
    }
  };

  useEffect(() => {
    async function fetchUserTeams() {
      if (!isSupabaseConfigured || !supabase || !currentUser?.id) return;
      try {
        const { data } = await supabase
          .from('team_members')
          .select('team_id, role, teams(name)')
          .eq('user_id', currentUser.id);

        if (data) {
          const currentTeamId = useStore.getState().teamId;
          const teams: UserTeam[] = data.map((row: any) => ({
            teamId: row.team_id,
            teamName: row.teams?.name || 'Unnamed Team',
            role: row.role || 'member',
            isCurrent: row.team_id === currentTeamId,
          }));
          setUserTeams(teams);
        }
      } catch (err) {
        console.error('Failed to fetch user teams:', err);
      }
    }
    fetchUserTeams();
  }, [currentUser?.id]);

  const copyInviteCode = () => {
    navigator.clipboard.writeText(teamConfig.inviteCode);
    setCopiedCode(true);
    toast.success('Invite code copied!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    const check = canAddMember();
    if (!check.can) {
      toast.error(check.reason || 'Team seat limit reached');
      return;
    }

    if (!newMember.name || !newMember.email) {
      toast.error('Name and Email are required');
      return;
    }

    addTeamMember({ 
      ...newMember, 
      presenceStatus: 'offline', 
      lastSeen: new Date().toISOString(),
      revenue: 0,
      avatar: '',
      dealsCount: 0,
      customStatus: ''
    });
    setNewMember({ name: '', role: '', email: '', phone: '', teamRole: 'member' });
    setShowAddMember(false);
    toast.success('Team member added');
  };

  const handleSwitchTeam = async (targetTeamId: string) => {
    try {
      switchToTeam(targetTeamId);
      toast.success('Switched team successfully');
      setShowTeamSwitcher(false);
    } catch (err) {
      toast.error('Failed to switch team');
    }
  };

  const handleCreateTeam = () => {
    setShowCreateModal(true);
    setShowTeamSwitcher(false);
  };

  return (
    <div className="space-y-8">
      {/* Header & Team Info */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-[2rem] bg-[var(--t-primary)] flex items-center justify-center text-white shadow-2xl relative group">
            <Building2 size={36} />
            <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-[var(--t-surface)] border border-[var(--t-border)] flex items-center justify-center text-[var(--t-primary)]">
              <Users size={16} />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-3xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">{teamConfig.name}</h1>
              <button 
                onClick={() => setShowTeamSwitcher(!showTeamSwitcher)}
                className="p-1.5 rounded-lg hover:bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] transition-colors"
              >
                <ChevronDown size={20} />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 py-1 px-3 rounded-full bg-[var(--t-surface-dim)] border border-[var(--t-border)]">
                <Users size={12} className="text-[var(--t-primary)]" />
                <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text)]">{team.length} / {teamConfig.maxSeats || 10} Seats</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowInvite(true)}
            className="px-6 py-3 bg-[var(--t-surface)] text-[var(--t-text)] font-black uppercase tracking-widest text-[10px] rounded-2xl border border-[var(--t-border)] hover:bg-[var(--t-surface-dim)] transition-all flex items-center gap-2"
          >
            <Users size={16} />
            Invite Member
          </button>
          <button
             onClick={() => navigate('/chat')}
             className="px-6 py-3 bg-[var(--t-primary)] text-white font-black uppercase tracking-widest text-[10px] rounded-2xl border border-white/10 shadow-xl shadow-[var(--t-primary-dim)] hover:translate-y-[-2px] active:translate-y-[0] transition-all flex items-center gap-2"
          >
            <MessageSquare size={16} />
            Team Chat
          </button>
        </div>
      </div>

      {/* Team Switcher Dropdown */}
      {showTeamSwitcher && (
        <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-3xl p-4 shadow-2xl animate-in zoom-in-95 duration-200 origin-top-left max-w-sm">
          <p className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] px-3 mb-3">Your Teams</p>
          <div className="space-y-1">
            {userTeams.map((t) => (
              <button
                key={t.teamId}
                onClick={() => !t.isCurrent && handleSwitchTeam(t.teamId)}
                className={`w-full flex items-center justify-between p-3 rounded-2xl transition-all ${t.isCurrent ? 'bg-[var(--t-primary)]/10 border border-[var(--t-primary)]' : 'hover:bg-[var(--t-surface-dim)] border border-transparent'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black ${t.isCurrent ? 'bg-[var(--t-primary)] text-white' : 'bg-[var(--t-surface-dim)] text-[var(--t-text-muted)]'}`}>
                    {t.teamName[0].toUpperCase()}
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-black ${t.isCurrent ? 'text-white' : 'text-[var(--t-text)]'}`}>{t.teamName}</p>
                    <p className="text-[10px] text-[var(--t-text-muted)] uppercase tracking-widest font-bold">{t.role}</p>
                  </div>
                </div>
                {t.isCurrent && <Check size={16} className="text-[var(--t-primary)]" />}
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--t-border)] grid grid-cols-2 gap-2">
            <button
               onClick={() => setShowJoinModal(true)}
               className="p-3 rounded-2xl bg-[var(--t-surface-dim)] text-[10px] font-black uppercase tracking-widest text-[var(--t-text)] hover:bg-[var(--t-surface-hover)] transition-all flex items-center justify-center gap-2"
            >
              <Zap size={14} className="text-[var(--t-primary)]" />
              Join
            </button>
            <button
               onClick={handleCreateTeam}
               className="p-3 rounded-2xl bg-[var(--t-surface-dim)] text-[10px] font-black uppercase tracking-widest text-[var(--t-text)] hover:bg-[var(--t-surface-hover)] transition-all flex items-center justify-center gap-2"
            >
              <Plus size={14} className="text-[var(--t-primary)]" />
              Create
            </button>
          </div>
        </div>
      )}

      {/* KPI & Milestones Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Performance Milestones */}
        <div className="rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Performance Milestones</h3>
              <p className="text-xs text-[var(--t-text-muted)]">Active team development goals.</p>
            </div>
            <button 
              onClick={() => setShowAddMilestone(true)}
              className="p-2 rounded-xl bg-[var(--t-primary)]/10 text-[var(--t-primary)] border border-[var(--t-primary)]/20 hover:bg-[var(--t-primary)] hover:text-white transition-all shadow-lg shadow-[var(--t-primary-dim)]"
            >
              <Plus size={20} />
            </button>
          </div>

          <div className="space-y-6">
            {milestones.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center space-y-4 bg-[var(--t-bg)]/50 rounded-3xl border-2 border-dashed border-[var(--t-border)]">
                <div className="w-16 h-16 rounded-2xl bg-[var(--t-surface)] border border-[var(--t-border)] flex items-center justify-center text-[var(--t-text-muted)]">
                  <Target size={32} />
                </div>
                <div>
                  <h4 className="text-lg font-black text-[var(--t-text)]">No Objectives Established</h4>
                  <p className="text-sm text-[var(--t-text-muted)] max-w-xs mx-auto">Create a fresh milestone to track performance starting from today.</p>
                </div>
                <button
                  onClick={() => setShowAddMilestone(true)}
                  className="px-6 py-3 bg-[var(--t-primary)] text-white font-black uppercase tracking-widest text-[10px] rounded-xl shadow-xl shadow-[var(--t-primary-dim)] hover:translate-y-[-2px] transition-all flex items-center gap-2"
                >
                  <Plus size={16} />
                  Create Your First Milestone
                </button>
              </div>
            ) : (
              (() => {
                const sortedMilestones = [...milestones].sort((a, b) => {
                  const aProgress = Math.min(100, Math.round((a.current / a.target) * 100));
                  const bProgress = Math.min(100, Math.round((b.current / b.target) * 100));
                  if (aProgress >= 100 && bProgress < 100) return 1;
                  if (aProgress < 100 && bProgress >= 100) return -1;
                  return 0;
                });

                return sortedMilestones.map((m) => {
                  const progress = Math.min(100, Math.round((m.current / m.target) * 100));
                  const countdown = getCountdown(m.targetDate);
                  const isJustCompleted = justCompleted[m.id];
                  
                  return (
                    <div key={m.id} className={`group p-6 rounded-3xl bg-[var(--t-bg)] border border-[var(--t-border)] hover:border-[var(--t-primary)]/50 transition-all ${isJustCompleted ? 'animate-[pulse_1s_ease-in-out_2]' : ''} relative overflow-hidden`}>
                      {isJustCompleted && (
                        <div className="absolute inset-0 bg-[var(--t-primary)]/10 z-0 animate-ping opacity-50" />
                      )}
                      <div className="flex items-start justify-between mb-4 relative z-10">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${progress >= 100 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-[var(--t-primary)]/10 text-[var(--t-primary)]'} border border-current/20`}>
                          {m.type === 'leads' ? <Zap size={20} /> : m.type === 'deals' ? <Target size={20} /> : <DollarSign size={20} />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-black text-[var(--t-text)] uppercase tracking-tight">{m.label}</h4>
                            {progress >= 100 && (
                              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 flex items-center gap-1 shadow-sm">
                                <Check size={10} strokeWidth={4} /> COMPLETED
                              </span>
                            )}
                            {m.startDate && progress < 100 && (
                              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-[var(--t-surface-dim)] text-[var(--t-text-muted)]">
                                SINCE {new Date(m.startDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-black text-[var(--t-text-muted)]">
                            <span className={progress >= 100 ? 'text-emerald-500' : 'text-[var(--t-primary)]'}>
                              {formatTarget(m.current, m.type)}
                            </span>
                            {' '}/ {formatTarget(m.target, m.type)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {countdown && (
                          <div className="px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                            <Clock size={12} />
                            {countdown}
                          </div>
                        )}
                        <button 
                          onClick={() => handleUpdateMilestone(m)}
                          className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] opacity-0 group-hover:opacity-100 transition-all hover:text-[var(--t-primary)]"
                          title="Edit Objective"
                        >
                          <Edit2 size={14} />
                        </button>
                        {m.type === 'other' && (
                          <button 
                            onClick={() => {
                              const newVal = prompt("Enter new current value:", m.current.toString());
                              if (newVal !== null) {
                                const val = parseInt(newVal);
                                if (!isNaN(val)) updateMilestone(m.id, { current: val });
                              }
                            }}
                            className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] opacity-0 group-hover:opacity-100 transition-all hover:text-[var(--t-primary)]"
                            title="Update Progress"
                          >
                            <Plus size={14} />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteMilestone(m.id)}
                          className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-rose-500 opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-500 hover:text-white"
                          title="Decommission Objective"
                        >
                          <UserMinus size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="h-3 w-full bg-[var(--t-bg)] rounded-full overflow-hidden p-0.5 border border-[var(--t-border)]">
                      <div 
                        className={`h-full bg-gradient-to-r ${progress >= 100 ? 'from-emerald-500 to-teal-400' : 'from-[var(--t-primary)] to-[var(--t-accent)]'} rounded-full transition-all duration-1000 relative`}
                        style={{ width: `${progress}%` }}
                      >
                        {progress > 5 && (
                          <div className="absolute right-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse shadow-sm" />
                        )}
                      </div>
                    </div>
                    
                    {/* Raise Goal Prompt */}
                    {progress >= 100 && (
                      <div className="mt-4 p-4 rounded-2xl bg-[var(--t-surface)] border border-[var(--t-primary)]/30 animate-in slide-in-from-top-2 relative z-10">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <Target size={16} className="text-[var(--t-primary)]" />
                            <span className="text-xs font-black uppercase tracking-widest text-[var(--t-text)]">Goal Achieved! Raise it?</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => updateMilestone(m.id, { target: Math.ceil(m.target * 1.1) })}
                              className="px-3 py-1.5 rounded-lg bg-[var(--t-surface-dim)] text-[10px] font-black text-[var(--t-text)] hover:bg-[var(--t-primary)] hover:text-white transition-colors"
                            >
                              +10%
                            </button>
                            <button
                              onClick={() => updateMilestone(m.id, { target: Math.ceil(m.target * 1.25) })}
                              className="px-3 py-1.5 rounded-lg bg-[var(--t-surface-dim)] text-[10px] font-black text-[var(--t-text)] hover:bg-[var(--t-primary)] hover:text-white transition-colors"
                            >
                              +25%
                            </button>
                            <button
                              onClick={() => updateMilestone(m.id, { target: m.target * 2 })}
                              className="px-3 py-1.5 rounded-lg bg-[var(--t-surface-dim)] text-[10px] font-black text-[var(--t-text)] hover:bg-[var(--t-primary)] hover:text-white transition-colors"
                            >
                              x2
                            </button>
                            <button
                              onClick={() => {
                                const newVal = prompt(`Enter new target for ${m.label}:`, m.target.toString());
                                if (newVal) {
                                  const val = parseInt(newVal.replace(/[^0-9]/g, ''));
                                  if (!isNaN(val) && val > m.target) {
                                    updateMilestone(m.id, { target: val });
                                  } else {
                                    toast.error('Invalid target. Must be greater than current target.');
                                  }
                                }
                              }}
                              className="px-3 py-1.5 rounded-lg bg-[var(--t-primary)]/10 text-[var(--t-primary)] border border-[var(--t-primary)]/20 text-[10px] font-black hover:bg-[var(--t-primary)] hover:text-white transition-all"
                            >
                              Custom
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
              })()
            )}
          </div>
        </div>

        {/* Revenue Mix */}
        <div className="rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
           <div className="flex items-center justify-between">
              <div>
                 <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Member Contributions</h3>
                 <p className="text-xs text-[var(--t-text-muted)]">Relative performance split.</p>
              </div>
              <DollarSign size={24} className="text-emerald-500" />
           </div>

           <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="h-[200px] w-[200px]">
                 <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                       <Pie
                          data={contributionData.slice(0, 5)}
                          cx="50%" cy="50%"
                          innerRadius={60} outerRadius={80}
                          paddingAngle={8}
                          dataKey="value"
                       >
                          {contributionData.slice(0, 5).map((_, i) => (
                             <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                          ))}
                       </Pie>
                       <Tooltip 
                         contentStyle={{ backgroundColor: 'var(--t-surface)', border: '1px solid var(--t-border)', borderRadius: '12px', color: 'var(--t-text)' }}
                         itemStyle={{ color: 'var(--t-text)', fontWeight: 'bold' }}
                       />
                    </PieChart>
                 </ResponsiveContainer>
              </div>

              <div className="flex-1 space-y-4">
                 {contributionData.slice(0, 5).map((member, i) => (
                    <div key={member.id} className="flex items-center justify-between">
                       <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-sm font-black text-[var(--t-text)]">{member.name}</span>
                       </div>
                       <span className="text-xs font-black text-emerald-500">{formatCurrency(member.rawRev)}</span>
                    </div>
                 ))}
              </div>
           </div>
        </div>
      </div>

      {/* Team Roster */}
      <div className="rounded-[2.5rem] p-8 liquid-glass border border-[var(--t-border-subtle)] shadow-xl space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Commanders & Operatives</h3>
            <p className="text-xs text-[var(--t-text-muted)]">Active roster management.</p>
          </div>
          <button
            onClick={() => setShowAddMember(true)}
            className="p-3 rounded-2xl bg-[var(--t-primary)]/10 text-[var(--t-primary)] border border-[var(--t-primary)]/20 hover:bg-[var(--t-primary)] hover:text-white transition-all shadow-lg shadow-[var(--t-primary-dim)]"
          >
            <Plus size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {team.map((member) => {
            const RoleIcon = ROLE_ICONS[member.teamRole] || Shield;
            const isExpanded = expandedMember === member.id;

            return (
              <div 
                key={member.id} 
                className={`p-6 rounded-[2rem] border transition-all duration-200 ${isExpanded ? 'bg-[var(--t-bg)] border-[var(--t-primary)] shadow-2xl scale-[1.02]' : 'bg-[var(--t-surface-dim)]/30 border-[var(--t-border)] hover:border-[var(--t-primary)]/30'}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-14 h-14 rounded-2xl bg-[var(--t-surface)] border border-[var(--t-border)] flex items-center justify-center text-xl font-black text-[var(--t-primary)] overflow-hidden shadow-xl">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          member.name[0].toUpperCase()
                        )}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-[var(--t-surface)] ${member.presenceStatus === 'online' ? 'bg-emerald-500 ring-2 ring-emerald-500/20' : 'bg-[var(--t-text-muted)]'}`} />
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-[var(--t-text)] leading-tight">{member.name}</h4>
                      <div className="flex items-center gap-1.5">
                        <RoleIcon size={12} className="text-[var(--t-primary)]" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">{member.teamRole}</span>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setExpandedMember(isExpanded ? null : member.id)}
                    className="p-2 rounded-xl hover:bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] transition-colors"
                  >
                    <ChevronDown size={20} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  <div className="bg-[var(--t-surface)] p-4 rounded-[1.5rem] border border-[var(--t-border)] shadow-sm hover:border-[var(--t-primary)]/40 transition-all group/stat">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-1 group-hover/stat:text-[var(--t-primary)] transition-colors">Won</p>
                    <p className="text-xl font-black text-[var(--t-text)]">{leads.filter(l => l.assignedTo === member.id && l.status === 'closed-won').length}</p>
                  </div>
                  <div className="bg-[var(--t-surface)] p-4 rounded-[1.5rem] border border-[var(--t-border)] shadow-sm hover:border-[var(--t-primary)]/40 transition-all group/stat">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-1 group-hover/stat:text-emerald-500 transition-colors">Revenue</p>
                    <p className="text-xl font-black text-emerald-500">{formatCurrency(leads.filter(l => l.assignedTo === member.id && ['closed-won', 'contract-in', 'under-contract'].includes(l.status)).reduce((sum, l) => sum + getDealValue(l), 0))}</p>
                  </div>
                  <div className="bg-[var(--t-surface)] p-4 rounded-[1.5rem] border border-[var(--t-border)] shadow-sm hover:border-[var(--t-primary)]/40 transition-all group/stat">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-1 group-hover/stat:text-blue-500 transition-colors">Win Rate</p>
                    <p className="text-xl font-black text-blue-500">
                      {(() => {
                        const won = leads.filter(l => l.assignedTo === member.id && l.status === 'closed-won').length;
                        const lost = leads.filter(l => l.assignedTo === member.id && l.status === 'closed-lost').length;
                        const total = won + lost;
                        return total > 0 ? Math.round((won / total) * 100) : 0;
                      })()}%
                    </p>
                  </div>
                  <div className="bg-[var(--t-surface)] p-4 rounded-[1.5rem] border border-[var(--t-border)] shadow-sm hover:border-[var(--t-primary)]/40 transition-all group/stat">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-1 group-hover/stat:text-indigo-500 transition-colors">Active</p>
                    <p className="text-xl font-black text-[var(--t-text)]">
                      {Math.floor((Date.now() - new Date(member.lastSeen || Date.now()).getTime()) / (1000 * 60 * 60 * 24))}d
                    </p>
                  </div>
                  <div className="bg-[var(--t-surface)] p-4 rounded-[1.5rem] border border-[var(--t-border)] shadow-sm hover:border-[var(--t-primary)]/40 transition-all group/stat">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-1 group-hover/stat:text-amber-500 transition-colors">Open</p>
                    <p className="text-xl font-black text-[var(--t-text)]">{leads.filter(l => l.assignedTo === member.id && !['closed-won', 'closed-lost'].includes(l.status)).length}</p>
                  </div>
                  <div className="bg-[var(--t-surface)] p-4 rounded-[1.5rem] border border-[var(--t-border)] shadow-sm hover:border-[var(--t-primary)]/40 transition-all group/stat">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-1 group-hover/stat:text-purple-500 transition-colors">Profit</p>
                    <p className="text-xl font-black text-emerald-600">
                      {formatCurrency(leads.filter(l => l.assignedTo === member.id && l.status === 'closed-won').reduce((sum, l) => sum + (Number(l.estimatedValue) * 0.15 || 0), 0))}
                    </p>
                  </div>
                </div>

                {isExpanded && (
                  <div className="space-y-4 animate-in slide-in-from-top-4 duration-200">
                    <div className="space-y-2">
                       <div className="flex items-center gap-1.5 text-[10px] text-[var(--t-text-muted)] font-bold uppercase">
                          <Users size={12} />
                          <span>{member.role || 'Operative'}</span>
                       </div>
                       <div className="flex items-center gap-1.5 text-[10px] text-[var(--t-text-muted)] font-bold uppercase">
                          <Plus size={12} />
                          <span>Joined {new Date(member.lastSeen).toLocaleDateString()}</span>
                       </div>
                    </div>
                    {currentUser?.id !== member.id && (
                      <div className="pt-4 border-t border-[var(--t-border)]">
                         <button
                           onClick={() => removeTeamMember(member.id)}
                           className="w-full py-3 rounded-xl bg-rose-500/10 text-rose-500 text-[10px] font-black uppercase tracking-widest border border-rose-500/20 hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center gap-2"
                         >
                           <UserMinus size={14} />
                           Release Member
                         </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm "
          onClick={() => setShowInvite(false)}
        >
          <div 
            className="bg-[var(--t-surface)] rounded-[2.5rem] border border-[var(--t-border)] p-8 max-w-md w-full shadow-2xl relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
             <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                <Users size={120} />
             </div>
             
             <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="text-2xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Invite Allies</h3>
                  <p className="text-sm text-[var(--t-text-muted)]">Expand your tactical operations.</p>
                </div>
                <button 
                   type="button"
                   onClick={() => setShowInvite(false)}
                   className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
             </div>

             <div className="space-y-6">
                <div className="p-6 rounded-3xl bg-[var(--t-bg)] border-2 border-dashed border-[var(--t-border)] text-center space-y-4">
                   <p className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">Tactical Join Code</p>
                   <p className="text-4xl font-black text-[var(--t-primary)] tracking-[0.2em]">{teamConfig.inviteCode}</p>
                   <button
                     onClick={copyInviteCode}
                     className="mx-auto flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--t-primary)] text-white text-[10px] font-black uppercase tracking-widest hover:translate-y-[-2px] transition-all"
                   >
                     {copiedCode ? <Check size={14} /> : <Copy size={14} />}
                     {copiedCode ? 'Copied!' : 'Copy Code'}
                   </button>
                </div>

                <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                   <p className="text-xs text-blue-400 leading-relaxed font-medium">
                      Members can use this code in their <strong>Team Settings</strong> to request access to join your roster.
                   </p>
                </div>
             </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm "
          onClick={() => setShowAddMember(false)}
        >
           <div 
             className="bg-[var(--t-surface)] rounded-[2.5rem] border border-[var(--t-border)] p-8 max-w-lg w-full shadow-2xl relative overflow-hidden"
             onClick={(e) => e.stopPropagation()}
           >
              <div className="flex justify-between items-start mb-8">
                 <div>
                   <h3 className="text-2xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Recruit Operative</h3>
                   <p className="text-sm text-[var(--t-text-muted)]">Direct enlistment into your team.</p>
                 </div>
                 <button 
                    type="button"
                    onClick={() => setShowAddMember(false)}
                    className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] hover:text-white transition-colors"
                 >
                   <X size={20} />
                 </button>
              </div>

              <form onSubmit={handleAddMember} className="space-y-6">
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Full Name</label>
                       <input
                         type="text"
                         required
                         value={newMember.name}
                         onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                         className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                         placeholder="John Doe"
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Email Address</label>
                       <input
                         type="email"
                         required
                         value={newMember.email}
                         onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                         className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                         placeholder="john@example.com"
                       />
                    </div>
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Job Title</label>
                       <input
                         type="text"
                         value={newMember.role}
                         onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                         className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                         placeholder="Sales Ops"
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">System Role</label>
                       <select
                         value={newMember.teamRole}
                         onChange={(e) => setNewMember({ ...newMember, teamRole: e.target.value as TeamRole })}
                         className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all appearance-none"
                       >
                          <option value="member">Operative (Member)</option>
                          <option value="admin">Commander (Admin)</option>
                          <option value="viewer">Observer (Viewer)</option>
                       </select>
                    </div>
                 </div>

                 <button
                   type="submit"
                   className="w-full py-4 bg-[var(--t-primary)] text-white font-black uppercase tracking-widest text-[12px] rounded-2xl shadow-xl shadow-[var(--t-primary-dim)] hover:translate-y-[-2px] transition-all"
                 >
                    Confirm Recruitment
                 </button>
              </form>
           </div>
        </div>
      )}

      {editingMilestone && (
        <div 
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-md overflow-y-auto"
          onClick={() => setEditingMilestone(null)}
        >
           <div 
             className="bg-[var(--t-surface)] rounded-[2.5rem] border border-[var(--t-border)] p-6 md:p-10 max-w-lg w-full shadow-[0_0_50px_rgba(0,0,0,0.5)] relative overflow-hidden my-auto"
             onClick={(e) => e.stopPropagation()}
           >
              <div className="flex justify-between items-start mb-8">
                 <div>
                   <h3 className="text-2xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">Edit Objective</h3>
                   <p className="text-sm text-[var(--t-text-muted)]">Modify Strategic target parameters.</p>
                 </div>
                 <button 
                    type="button"
                    onClick={() => setEditingMilestone(null)}
                    className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] hover:text-white transition-colors"
                 >
                   <X size={20} />
                 </button>
              </div>
  
              <form onSubmit={handleSaveEditMilestone} className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Objective Label</label>
                    <input
                      type="text"
                      required
                      value={editingMilestone.label}
                      onChange={(e) => setEditingMilestone({ ...editingMilestone, label: e.target.value })}
                      className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                    />
                 </div>
  
                 <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Target Value</label>
                        <input
                          type="number"
                          required
                          value={editingMilestone.target}
                          onChange={(e) => setEditingMilestone({ ...editingMilestone, target: parseInt(e.target.value) || 0 })}
                          className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                        />
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Metric Category</label>
                        <select
                          value={editingMilestone.type}
                          onChange={(e) => setEditingMilestone({ ...editingMilestone, type: e.target.value as any })}
                          className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all appearance-none"
                        >
                           <option value="leads">Leads Generated</option>
                           <option value="deals">Deals Closed</option>
                           <option value="revenue">Total Revenue</option>
                           <option value="other">Other Tactical Metric</option>
                        </select>
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Current Value</label>
                        <input
                          type="number"
                          value={editingMilestone.current}
                          onChange={(e) => setEditingMilestone({ ...editingMilestone, current: parseInt(e.target.value) || 0 })}
                          className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                        />
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Target Date (Optional)</label>
                        <div className="relative">
                           <input
                             type="date"
                             value={editingMilestone.targetDate || ''}
                             onChange={(e) => setEditingMilestone({ ...editingMilestone, targetDate: e.target.value })}
                             className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                           />
                           <Calendar size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] pointer-events-none" />
                        </div>
                     </div>
                  </div>
  
                 <button
                   type="submit"
                   className="w-full py-4 bg-[var(--t-primary)] text-white font-black uppercase tracking-widest text-[12px] rounded-2xl shadow-xl shadow-[var(--t-primary-dim)] hover:translate-y-[-2px] transition-all"
                 >
                    Save Changes
                 </button>
              </form>
           </div>
        </div>
      )}

      {showAddMilestone && (
        <div 
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-md overflow-y-auto"
          onClick={() => setShowAddMilestone(false)}
        >
           <div 
             className="bg-[var(--t-surface)] rounded-[2.5rem] border border-[var(--t-border)] p-6 md:p-10 max-w-lg w-full shadow-[0_0_50px_rgba(0,0,0,0.5)] relative overflow-hidden my-auto"
             onClick={(e) => e.stopPropagation()}
           >
              <div className="flex justify-between items-start mb-8">
                 <div>
                   <h3 className="text-2xl font-black italic uppercase tracking-tighter text-[var(--t-text)]">New Objective</h3>
                   <p className="text-sm text-[var(--t-text-muted)]">Strategic goal for the command center.</p>
                 </div>
                 <button 
                    type="button"
                    onClick={() => setShowAddMilestone(false)}
                    className="p-2 rounded-xl bg-[var(--t-surface-dim)] text-[var(--t-text-muted)] hover:text-white transition-colors"
                 >
                   <X size={20} />
                 </button>
              </div>
 
              <form onSubmit={handleAddMilestone} className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Objective Label</label>
                    <input
                      type="text"
                      required
                      value={newMilestone.label}
                      onChange={(e) => setNewMilestone({ ...newMilestone, label: e.target.value })}
                      className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                      placeholder="e.g. Monthly Revenue Goal"
                    />
                 </div>
 
                 <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Target Value</label>
                        <input
                          type="number"
                          required
                          value={newMilestone.target || ''}
                          onChange={(e) => setNewMilestone({ ...newMilestone, target: parseInt(e.target.value) || 0 })}
                          className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                          placeholder="100000"
                        />
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Metric Category</label>
                        <select
                          value={newMilestone.type}
                          onChange={(e) => setNewMilestone({ ...newMilestone, type: e.target.value as any })}
                          className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all appearance-none"
                        >
                           <option value="leads">Leads Generated</option>
                           <option value="deals">Deals Closed</option>
                           <option value="revenue">Total Revenue</option>
                           <option value="other">Other Tactical Metric</option>
                        </select>
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Start Date (From Now)</label>
                       <div className="relative">
                          <input
                            type="date"
                            value={newMilestone.startDate || ''}
                            onChange={(e) => setNewMilestone({ ...newMilestone, startDate: e.target.value })}
                            className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                          />
                          <Calendar size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] pointer-events-none" />
                       </div>
                       <p className="text-[9px] text-[var(--t-text-muted)] ml-2 italic">Only counts data created after this date.</p>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)] ml-2">Target Date (Optional)</label>
                       <div className="relative">
                          <input
                            type="date"
                            value={newMilestone.targetDate || ''}
                            onChange={(e) => setNewMilestone({ ...newMilestone, targetDate: e.target.value })}
                            className="w-full bg-[var(--t-bg)] border border-[var(--t-border)] rounded-2xl py-3 px-4 text-[var(--t-text)] outline-none focus:border-[var(--t-primary)] transition-all"
                          />
                          <Calendar size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)] pointer-events-none" />
                       </div>
                       <p className="text-[9px] text-[var(--t-text-muted)] ml-2 italic">Adds a live countdown timer.</p>
                    </div>
                  </div>
 
                 <button
                   type="submit"
                   className="w-full py-4 bg-[var(--t-primary)] text-white font-black uppercase tracking-widest text-[12px] rounded-2xl shadow-xl shadow-[var(--t-primary-dim)] hover:translate-y-[-2px] transition-all"
                 >
                    Establish Objective
                 </button>
              </form>
           </div>
        </div>
      )}
 
      {/* Team Switcher & Creation Modals */}
      <JoinTeamModal isOpen={showJoinModal} onClose={() => setShowJoinModal(false)} />
      <CreateTeamModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </div>
  );
}


